import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, Check } from 'lucide-react';

// This screen lets a buyer choose WHICH of the seller's own registered payment
// accounts to send fiat to (relevant when a seller has multiple accounts, e.g.
// both M-PESA and a bank account, registered on the same ad). It has never
// fetched or shown the current user's own saved payment methods -- that would be
// meaningless here, since the buyer isn't paying themselves. The methods list
// comes entirely from OrderPlacement via navigation state (the ad's own
// paymentMethods, already fetched with the ad).
export default function SelectPaymentMethod() {
    const navigate = useNavigate();
    const location = useLocation();
    const methods: any[] = (location.state as any)?.methods ?? [];
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<any>(methods[0] ?? null);

    const filtered = methods.filter((m: any) =>
        !search ||
        m.methodType?.toLowerCase().includes(search.toLowerCase()) ||
        m.accountName?.toLowerCase().includes(search.toLowerCase()) ||
        (m.extraFields?.bankName || m.bankName || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleConfirm = () => {
        if (!selected) return;
        sessionStorage.setItem('p2p_pm_selected', JSON.stringify(selected));
        navigate(-1);
    };

    const MethodRow = ({ m }: { m: any }) => {
        const isSelected = selected?._id === m._id;
        const bankName = m.extraFields?.bankName || m.bankName || '';
        return (
            <div
                onClick={() => setSelected(m)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--p2p-border-default)', cursor: 'pointer', background: isSelected ? 'var(--p2p-buy-subtle)' : 'transparent', transition: 'background 0.15s' }}
            >
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: m.methodColor || '#2563eb', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--p2p-text-primary)' }}>
                            {bankName || m.methodType}
                        </span>
                        {bankName && (
                            <span style={{ fontSize: 11, fontWeight: 500, background: 'var(--p2p-bg-elevated)', border: '1px solid var(--p2p-border-input)', color: 'var(--p2p-text-muted)', borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>
                                {m.methodType}
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>
                        {m.accountName}
                        {/* Full account/phone number is intentionally not shown here --
                            it's only revealed on the active order screen after the
                            order is created, matching escrow-style P2P platforms. */}
                    </div>
                </div>
                {isSelected && (
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--p2p-buy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Check size={13} color="#000" />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ background: 'var(--p2p-bg-primary)', height: 'calc(100dvh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="p2p-header" style={{ flexShrink: 0 }}>
                <button className="p2p-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} />Pay Seller Via
                </button>
            </div>

            {methods.length > 1 && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)', flexShrink: 0 }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--p2p-text-muted)' }} />
                        <input className="p2p-input" style={{ paddingLeft: 36 }} placeholder="Search payment method..." value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                    <div className="p2p-empty">
                        <p className="p2p-empty-text">
                            {methods.length === 0
                                ? 'This seller has no payment methods on this ad -- they may need to update it.'
                                : 'No results.'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={{ padding: '10px 16px 4px' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--p2p-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Seller Accepts
                            </div>
                        </div>
                        {filtered.map((m: any) => <MethodRow key={m._id} m={m} />)}
                    </>
                )}
            </div>

            <div style={{ flexShrink: 0, padding: 16, background: 'var(--p2p-bg-primary)', borderTop: '1px solid var(--p2p-border-default)', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
                <button className="p2p-btn-yellow" disabled={!selected} onClick={handleConfirm}>
                    Confirm
                </button>
            </div>
        </div>
    );
}
